import {
  Anchor,
  Button,
  FlexCell,
  FlexRow,
  FlexSpacer,
  LabeledInput,
  TextArea,
  TextInput,
} from '@epam/loveship'
import { useUuiContext, UuiContexts } from '@epam/uui-core'

import { SidebarPanelHeader } from '../../../../../../components/SidebarPanel/components/SidebarPanelHeader'
import { SidebarPanelMain } from '../../../../../../components/SidebarPanel/components/SidebarPanelMain'
import { ExtendedEvent } from '../../../../../../constants/types'
import { showConfirmEventDeleteModal } from '../../../../../../utils/modals/showConfirmEventDeleteModal'
import { useSideBarControlls } from '../../../../../../hooks/useSideBarControlls'
import { useDeleteEventMutation } from '../../../../../../store'
import { useAppSelector } from '../../../../../../hooks/redux/redux'
import { useGetCategoriesQuery } from '../../../../../../store'
import { getExtendedEvent } from '../../../../../../utils/common/getExtendedEvent'
import { useActions } from '../../../../../../hooks/redux/actions'
import css from './EventDetails.module.scss'
import { LOCATION_NOTFOUND_MESSAGE } from '../../../../../../constants/values'

type EventDetailsProps = { event: ExtendedEvent }

export const EventDetails = ({ event: incomingEvent }: EventDetailsProps) => {
  const { id } = incomingEvent
  const { currentEvents } = useAppSelector((state) => state.events)
  const { data: categories } = useGetCategoriesQuery()

  const event = getExtendedEvent(
    currentEvents?.find((event) => event.id === id) ?? incomingEvent,
    categories
  )
  const {
    categoryName,
    parsedDate,
    parsedTime,
    name,
    languageName,
    details,
    link,
    location,
    organizers,
  } = event

  const svc = useUuiContext<UuiContexts>()
  const [deleteEvent] = useDeleteEventMutation()
  const { setSelectedEvent, setSelectedEventId } = useActions()

  const isLinkFieldVisible = !!link?.length

  const { showEventsPanel, showEventEdit } = useSideBarControlls()

  const { userInfo: user } = useAppSelector((state) => state.users)
  const isEditModeEnabled = !!organizers?.filter(
    (organizer) => organizer?.id === user?.id
  ).length

  const { removeCurrentEvent } = useActions()

  const onDelete = (eventId: number) => {
    const deleteEventHandler = () => {
      setSelectedEvent(null)
      setSelectedEventId(null)
      return deleteEvent(eventId)
        .unwrap()
        .then(() => {
          removeCurrentEvent(eventId)
        })
    }

    showConfirmEventDeleteModal(svc, deleteEventHandler).then(() =>
      showEventsPanel()
    )
  }

  return (
    <>
      <SidebarPanelHeader
        title="Event info"
        onClose={showEventsPanel}
        withCloseIcon
      />

      <SidebarPanelMain>
        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput label="Event Category">
              <TextInput
                isReadonly
                value={categoryName}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Event Name">
              <TextArea
                autoSize
                isReadonly={true}
                value={name}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput label="Event Language">
              <TextInput
                isReadonly
                value={languageName}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput label="Event date">
              <TextInput
                isReadonly
                value={parsedDate}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput label="Event time">
              <TextInput
                isReadonly
                value={parsedTime}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Event Location">
              <TextArea
                isReadonly
                autoSize
                rows={1}
                placeholder="Event Location"
                value={location || LOCATION_NOTFOUND_MESSAGE}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        {/* <FlexRow>
                    <Anchor
                        rawProps={{ 'aria-label': 'Google Maps' }}
                        href={googleMapsUrl}
                        target="_blank"
                    >
                        <Text size="36" font="sans" color="night700">
                            <MapIcon style={{ height: 12, width: 12 }} /> Open in Google Maps
                        </Text>
                    </Anchor>
                    <FlexSpacer />
                    {isEditModeEnabled && (
                        <FlexRow spacing="6">
                            <IconButton
                                icon={EditIcon}
                                onClick={() => showEventEdit(event)}
                            />
                            <IconButton
                                icon={DeleteIcon}
                                onClick={() => onDelete(event.id)}
                            />
                        </FlexRow>)}
                </FlexRow> */}

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Event Description">
              <TextArea
                autoSize
                isReadonly={true}
                placeholder="Event Description"
                value={details}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        {isLinkFieldVisible && (
          <FlexRow vPadding="12">
            <FlexCell grow={1} cx={css.eventLinkBlock}>
              <LabeledInput label="Event link">
                <Anchor cx={css.linkStyle} href={link} target="_blank">
                  <TextInput isReadonly value={link} onValueChange={() => {}} />
                </Anchor>
              </LabeledInput>
            </FlexCell>
          </FlexRow>
        )}

        {isEditModeEnabled && (
          <FlexRow
            cx={css.footer}
            // topShadow
            columnGap="6"
          >
            <FlexSpacer />
            <Button
              size="36"
              color="sky"
              fill="white"
              caption="Delete event"
              onClick={() => onDelete(event.id)}
            />
            <Button
              size="36"
              color="sky"
              caption="Edit information"
              onClick={() => showEventEdit(event)}
            />
          </FlexRow>
        )}

        <div className={css.bottomSeparator}></div>
      </SidebarPanelMain>
    </>
  )
}
